import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';
import { buildInfo } from 'src/environments/version';

/**
 * Environments where the build stamp is restricted to superadmins. Anything else
 * (a developer machine, whatever `envName` they set in their local
 * environment.ts) shows it to everyone: fail-closed on deployed environments,
 * fail-open locally.
 */
const RESTRICTED_ENVS = ['test', 'preprod', 'prod'];

/**
 * Discreet build stamp: environment + short commit, with the full build details
 * on hover and copied to the clipboard on click (so support can ask "which
 * version are you on?" and get an unambiguous answer).
 *
 * There is no hand-maintained version number on purpose — see
 * frontend/scripts/generate-version.js.
 */
@Component({
  selector: 'app-version',
  imports: [MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
    <button
      type="button"
      class="app-version"
      [matTooltip]="tooltip()"
      matTooltipPosition="above"
      (click)="copyDetails()"
    >
      {{ label }}
    </button>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        flex: 0 0 auto;
      }

      /* Collapsed sidebar is too narrow to read a build stamp. */
      :host-context(.sidebarNav-mini) {
        display: none;
      }

      /* Inline variant, for the horizontal layout header. */
      :host(.compact) {
        display: inline-flex;
        align-items: center;
      }

      :host(.compact) .app-version {
        width: auto;
        padding: 0 8px;
      }

      .app-version {
        display: block;
        width: 100%;
        padding: 8px 24px 12px;
        border: 0;
        background: none;
        font-size: 11px;
        line-height: 1.2;
        letter-spacing: 0.02em;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--mat-sys-outline);
        opacity: 0.75;
        cursor: pointer;
        transition: opacity 0.15s ease;
      }

      .app-version:hover,
      .app-version:focus-visible {
        opacity: 1;
      }
    `,
  ],
})
export class AppVersionComponent {
  private readonly authService = inject(AuthService);
  private readonly clipboard = inject(Clipboard);

  /** e.g. `test · c79562b` — a trailing `*` means the build had uncommitted changes. */
  readonly label = `${environment.envName} · ${buildInfo.commit}${buildInfo.dirty ? '*' : ''}`;

  /**
   * Everyone in local dev; superadmins only on test / preprod / prod. Reads the
   * `currentUser` signal through AuthService, so it settles on its own once the
   * session is restored.
   */
  readonly visible = computed(
    () => !RESTRICTED_ENVS.includes(environment.envName) || this.authService.isSuperAdmin()
  );

  private readonly copied = signal(false);

  readonly tooltip = computed(() => (this.copied() ? 'Copied to clipboard' : this.details));

  private readonly details = [
    `Environment: ${environment.envName}`,
    `Branch: ${buildInfo.branch}`,
    `Commit: ${buildInfo.commit}${buildInfo.dirty ? ' (uncommitted changes)' : ''}`,
    `Built: ${this.formatBuildDate()}`,
  ].join(' · ');

  copyDetails(): void {
    this.clipboard.copy(this.details);
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
  }

  private formatBuildDate(): string {
    const date = new Date(buildInfo.buildDate);
    if (Number.isNaN(date.getTime())) {
      return buildInfo.buildDate;
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
